import React, { memo, useCallback, useMemo, useRef } from 'react'
import { Grid } from '@mui/material'
import {
    AnyDateRange,
    AnySelect,
    AnySelectOption,
    computeDateRange,
    DateRangeItemNames,
    DateRangeValue,
    useValidatedForm,
} from '../anyform'
import { EMBED_ANALYTICS_COLORS } from '../components'
import { ANALYTICS_PLATFORMS, AnalyticsPlatform } from '../model'
import { translate } from '../i18n'
import {
    AnalyticsFilters,
    ProjectAnalyticsFiltersProps,
} from './ProjectAnalyticsFilters.types'

export const DEFAULT_ANALYTICS_RANGE: DateRangeItemNames = 'last_30_days'

const DATE_RANGE_MENU_ITEMS: DateRangeItemNames[] = [
    'today',
    'yesterday',
    'last_7_days',
    'last_30_days',
    'year_to_date',
    'last_12_months',
    'all_time',
    'custom',
]

// Seeds the parent's initial filter state so the first fetch already matches
// the picker's default range (avoids an extra all-time request on mount).
export const defaultAnalyticsFilters = (): AnalyticsFilters => {
    const { startDate, endDate } = computeDateRange(DEFAULT_ANALYTICS_RANGE)
    return {
        from: startDate ? startDate.toISOString() : undefined,
        to: endDate ? endDate.toISOString() : undefined,
    }
}

function ProjectAnalyticsFilters({
    onChange,
    embedded = false,
}: ProjectAnalyticsFiltersProps) {
    const { register } = useValidatedForm<AnalyticsFilters>(false)
    const filters = useRef<AnalyticsFilters>(defaultAnalyticsFilters())

    // 'all' is a sentinel (not sent to the API): MUI's Select renders an empty
    // display for a '' value, which hid the default "All platforms" label.
    const platformOptions = useMemo<AnySelectOption[]>(
        () => [
            {
                value: 'all',
                label: translate('projects.analytics.platforms.all') as string,
            },
            ...ANALYTICS_PLATFORMS.map((platform) => ({
                value: platform,
                label: translate(
                    `projects.analytics.platforms.${platform}`
                ) as string,
            })),
        ],
        []
    )

    const emit = useCallback(() => {
        onChange({ ...filters.current })
    }, [onChange])

    const onDateRangeChange = useCallback(
        (_name: string, value?: DateRangeValue) => {
            filters.current.from = value?.startDate
                ? value.startDate.toISOString()
                : undefined
            filters.current.to = value?.endDate
                ? value.endDate.toISOString()
                : undefined
            emit()
        },
        [emit]
    )

    const onPlatformChange = useCallback(
        (_name: string, value?: string) => {
            filters.current.platform =
                value && value !== 'all'
                    ? (value as AnalyticsPlatform)
                    : undefined
            emit()
        },
        [emit]
    )

    return (
        <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={7}>
                <AnyDateRange
                    name="range"
                    label={translate('projects.analytics.date_range') as string}
                    register={register}
                    notifier={onDateRangeChange}
                    menuItemsCustom={DATE_RANGE_MENU_ITEMS}
                    defaultRange={DEFAULT_ANALYTICS_RANGE}
                    rangeColors={
                        embedded ? [EMBED_ANALYTICS_COLORS.data] : undefined
                    }
                />
            </Grid>
            <Grid item xs={12} sm={5}>
                <AnySelect
                    name="platform"
                    label={translate('projects.analytics.platform') as string}
                    initialValue="all"
                    options={platformOptions}
                    register={register}
                    notifier={onPlatformChange}
                />
            </Grid>
        </Grid>
    )
}

export default memo(ProjectAnalyticsFilters)
